import { Component, Input, NgZone, ViewChild } from "@angular/core";
import { AppVoData } from "@app/app-vo-data";
import { AppRouter } from "@app/app.router";
import { ENV } from "@env";
import { UserConverter } from "@utils/converter/user-converter";
import { Events, NavController } from "ionic-angular";
import {
  AuthenticationProvider,
  AuthenticationProviderEvent,
  BackgroundStyle,
  CheckUtils,
  ConversationsSettingProvider,
  ConversationsSettingProviderEvent,
  CustomerAccessPolicyVoData,
  CustomerAppProvider,
  CustomerAppProviderEvent,
  CustomerAppTypesVo,
  CustomerAppVoData,
  MessengerProviderEvent,
  OrganizationDetailsProvider,
  OrganizationDetailsProviderEvent,
  OrganizationProvider,
  UpdatedAllModelVoData,
  UpdatedModelVoData,
  UsersProvider,
  UserVoData
} from "od-ts";
import { DecoratedAvatarData, Pair, TabData } from "ui-ts";

@Component({
  selector: "od-com-header",
  templateUrl: "header.html"
})
export class Header {
  private _nav: NavController;
  private zone: NgZone;
  private _goToPage: string;
  private _decoratedAvatarData: DecoratedAvatarData;
  private _title: string;
  private _tabDatas: Array<TabData>;
  private _datas: Map<Pair<number, string>, TabData>;
  private _activated: TabData;

  private _mainColor: string;
  private _mainTextColor: string;
  private _secondaryButtonColor: string;
  private _secondaryButtonTextColor: string;
  private _bodyColor: string;

  private _isWelcome: boolean;
  private _orgLogo: string;
  private _welcomeTitle: string;
  private _welcomeDescription: string;
  private _showWelcomeLogo: boolean;
  private _welcomeLogoStyle: string;
  private _welcomeLogoRepeat: string;
  private _welcomeLogoUrl: string;

  @ViewChild("odTabs") odTabs;

  private _mainLoadedHandler: () => void;
  private _chatSettingsLoadedHandler: () => void;
  private _userHandler: (user) => void;
  private _navHandler: () => void;
  private _userUpdatedHandler: (updatesData: UpdatedModelVoData<number>) => any;
  private _usersUpdatedHandler: (updatesData: UpdatedAllModelVoData<number>) => any;
  private _colorsUpdater: () => any;

  constructor(private _conversationsSettingProvider: ConversationsSettingProvider,
              private _organizationDetailsProvider: OrganizationDetailsProvider,
              private _orgProvider: OrganizationProvider,
              private _authProvider: AuthenticationProvider,
              private _usersProvider: UsersProvider,
              private _customerAppProvider: CustomerAppProvider,
              private _router: AppRouter,
              private _events: Events) {
    this.setup();
    this._chatSettingsLoadedHandler();
  }

  @Input() set nav(nav: NavController) {
    this.unbind();
    this._nav = nav;
    this.bind();
  }

  private setup() {
    this.zone = new NgZone({ enableLongStackTrace: false });
    this._datas = new Map();
    this._activated = null;

    this._mainLoadedHandler = () => this.zone.run(() => {
      if (this._customerAppProvider.ready) {
        let allowIds: AppVoData[] = document["allowApps"];

        this._datas.clear();

        let _CustomerAppTypesVo: Array<CustomerAppVoData> = this._customerAppProvider.model.getAll();
        if (!CheckUtils.isEmpty(_CustomerAppTypesVo))
          for (let _customerApp of _CustomerAppTypesVo) {
            if ((allowIds == null || this.existApp(allowIds, _customerApp.uid, ["cv", "ms", "cp", "oa"])) && (
              _customerApp.accessPolicy === CustomerAccessPolicyVoData.ANONYMOUS_AND_LOGIN ||
              this._authProvider.isAuthenticated())) {
              this.addTabData(_customerApp.uid,
                _customerApp.type,
                _customerApp.name,
                _customerApp.activated,
                _customerApp.position);
            }
          }
      }
      this._tabDatas = Array.from(this._datas.values());
    });

    this._chatSettingsLoadedHandler = () => this.zone.run(() => {
      if (this._conversationsSettingProvider.ready) {
        this._welcomeTitle = this._conversationsSettingProvider.model.welcomeTitle;
        this._welcomeDescription = this._conversationsSettingProvider.model.welcomeDescription;
        this._showWelcomeLogo = this._conversationsSettingProvider.model.showWelcomeLogo;
        switch (this._conversationsSettingProvider.model.welcomeLogoStyle) {
          case BackgroundStyle.CONTAIN_REPEAT:
            this._welcomeLogoStyle = "contain";
            this._welcomeLogoRepeat = "repeat";
            break;
          default:
            if (this._conversationsSettingProvider.model.welcomeLogoStyle != null)
              this._welcomeLogoStyle = this._conversationsSettingProvider.model.welcomeLogoStyle.toLowerCase();
            this._welcomeLogoRepeat = "no-repeat";
            break;
        }
        this._welcomeLogoUrl = "url(\"" + this._conversationsSettingProvider.model.welcomeLogoUrl + "\"";
      }

      this._colorsUpdater = () => {
        if (this._organizationDetailsProvider.ready) {
          let appearance = this._organizationDetailsProvider.model.appearanceOptions;
          this._orgLogo = "url(\"" + ENV.fileUrl + this._organizationDetailsProvider.model.logo + "\"";
          this._mainColor = appearance.mainColor ? "#" + appearance.mainColor.toString() : this._mainColor;
          this._mainTextColor = appearance.mainTextColor ? "#" + appearance.mainTextColor.toString() : this._mainTextColor;
          this._secondaryButtonColor = appearance.secondaryButtonColor ? "#" + appearance.secondaryButtonColor.toString() : this._secondaryButtonColor;
          this._secondaryButtonTextColor = appearance.secondaryButtonTextColor ? "#" + appearance.secondaryButtonTextColor.toString() : this._secondaryButtonTextColor;
          this._bodyColor = appearance.bodyColor ? "#" + appearance.bodyColor.toString() : this._bodyColor;
          this.applyColors();
        }
      };
    });

    this._userHandler = user => this.zone.run(() => {
      this.renderDecoratedAvatar(user);
      this._mainLoadedHandler();
    });

    this._userUpdatedHandler = (updatesData: UpdatedModelVoData<number>) => this.zone.run(() => this.userUpdated(updatesData));
    this._usersUpdatedHandler = (updatesData: UpdatedAllModelVoData<number>) => this.zone.run(() => this.usersUpdated(updatesData));

    this._navHandler = () => {
      if (CheckUtils.isEmpty(this._nav.getActive()) || CheckUtils.isEmpty(this._nav.getActive().component)) return;
      this._goToPage = null;

      let component = this._nav.getActive().component;
      let data = this._nav.getActive().data;

      this._isWelcome = "WelcomePage" === component.name;

      if (this._isWelcome) {
        this._title = "";
        this._activated = this.getTabData(null, CustomerAppTypesVo.MESSENGER);
      } else if ("ConversationsPage" === component.name) {
        this._title = "Conversations.title";
        this._activated = this.getTabData(null, CustomerAppTypesVo.MESSENGER);
      } else if ("MessagesPage" === component.name) {
        this._title = "Messages.title";
        this._goToPage = "ConversationsPage";
        this._activated = this.getTabData(null, CustomerAppTypesVo.MESSENGER);
      } else if ("CustomerPortalPage" === component.name) {
        this._title = "CustomerPortal.title";
        this._activated = this.getTabData(data.id, CustomerAppTypesVo.CUSTOMER_PORTAL);
      } else if ("OtherAppPage" === component.name) {
        this._title = "OtherApp.title";
        this._activated = this.getTabData(data.id, null);
      } else if ("SignInPage" === component.name) {
        this._title = "SignIn.title";
        if (!CheckUtils.isEmpty(data)) {
          if ("ConversationsPage" === data.from) {
            this._activated = this.getTabData(null, CustomerAppTypesVo.MESSENGER);
          } else if ("MessagesPage" === data.from) {
            this._activated = this.getTabData(null, CustomerAppTypesVo.MESSENGER);
          } else if ("CustomerPortalPage" === data.from) {
            this._activated = this.getTabData(data.id, CustomerAppTypesVo.CUSTOMER_PORTAL);
          }
        }
      } else if ("ProfilePage" === component.name) {
        this._title = "Profile.title";
        this._activated = null;
      } else if ("SignUpPage" === component.name) {
        this._title = "SignUp.title";
        this._goToPage = "SignInPage";
        this._activated = null;
      } else if ("ResetPasswordPage" === component.name) {
        this._title = "ResetPassword.title";
        this._goToPage = "SignInPage";
        this._activated = null;
      } else {
        this._title = "";
        this._activated = null;
      }
    };
  }

  private existApp(allowApps: AppVoData[], id: number, types: string[]): boolean {
    for (let allowApp of allowApps) {
      if (allowApp.id == id && types.indexOf(allowApp.type) !== -1) return true;
    }
    return types == null || types.length === 0;
  }

  private bind() {
    this._events.subscribe(MessengerProviderEvent.READY, this._mainLoadedHandler);
    this._events.subscribe(CustomerAppProviderEvent.READY, this._mainLoadedHandler);
    this._events.subscribe(AuthenticationProviderEvent.AUTO_SIGNED, this._mainLoadedHandler);
    this._events.subscribe(ConversationsSettingProviderEvent.READY, this._chatSettingsLoadedHandler);
    this._events.subscribe(OrganizationDetailsProviderEvent.READY, this._colorsUpdater);
    this._events.subscribe(AuthenticationProviderEvent.SIGNED, this._mainLoadedHandler);
    this._events.subscribe(AuthenticationProviderEvent.USER_SIGNED, this._userHandler);
    this._events.subscribe(this._usersProvider.model.event.UPDATED, this._userUpdatedHandler);
    this._events.subscribe(this._usersProvider.model.event.UPDATED_ALL, this._usersUpdatedHandler);
    if (!CheckUtils.isEmpty(this._nav)) this._nav.viewWillEnter.subscribe(this._navHandler);
  }

  private unbind() {
    this._events.unsubscribe(MessengerProviderEvent.READY, this._mainLoadedHandler);
    this._events.unsubscribe(CustomerAppProviderEvent.READY, this._mainLoadedHandler);
    this._events.unsubscribe(AuthenticationProviderEvent.AUTO_SIGNED, this._mainLoadedHandler);
    this._events.unsubscribe(ConversationsSettingProviderEvent.READY, this._chatSettingsLoadedHandler);
    this._events.unsubscribe(AuthenticationProviderEvent.SIGNED, this._mainLoadedHandler);
    this._events.unsubscribe(AuthenticationProviderEvent.USER_SIGNED, this._userHandler);
    this._events.unsubscribe(this._usersProvider.model.event.UPDATED, this._userUpdatedHandler);
    this._events.unsubscribe(this._usersProvider.model.event.UPDATED_ALL, this._usersUpdatedHandler);
    this._events.unsubscribe(OrganizationDetailsProviderEvent.READY, this._colorsUpdater);
    if (!CheckUtils.isEmpty(this._nav)) this._nav.viewWillEnter.unsubscribe();
  }

  ionViewDidLeave() {
    this.unbind();
  }

  onSelect(data: TabData) {
    if (CheckUtils.isEmpty(data)) return;
    switch (data.type) {
      case CustomerAppTypesVo.MESSENGER:
        this._router.push("ConversationsPage");
        break;
      case CustomerAppTypesVo.WORK_ITEM_CUSTOMS_4:
      case CustomerAppTypesVo.WORK_ITEM_CUSTOMS_5:
      case CustomerAppTypesVo.KNOWLEDGE_BASE:
      case CustomerAppTypesVo.TASKS:
      case CustomerAppTypesVo.FEATURES:
      case CustomerAppTypesVo.TICKETS:
      case CustomerAppTypesVo.WEB_FORMS:
      case CustomerAppTypesVo.WEB_PAGES:
        if (data.id != null && this._customerAppProvider.model.has(data.id) &&
          this._authProvider.isAuthenticated() === false &&
          this._customerAppProvider.model.get(data.id).accessPolicy == CustomerAccessPolicyVoData.LOGIN) {
          this._router.push("SignInPage", { id: data.id });
        } else {
          this._router.push("OtherAppPage", { id: data.id });
        }
        break;
      case CustomerAppTypesVo.CUSTOMER_PORTAL:
        if (data.id != null && this._customerAppProvider.model.has(data.id) &&
          this._authProvider.isAuthenticated() === false &&
          this._customerAppProvider.model.get(data.id).accessPolicy == CustomerAccessPolicyVoData.LOGIN) {
          this._router.push("SignInPage", { id: data.id });
        } else {
          this._router.push("CustomerPortalPage", { id: data.id });
        }
        break;
    }
  }

  goto(page: string) {
    if (page != null) this._router.push(page);
  }

  onClickOnPrevious(e: number) {
    this.odTabs.scrollTo = (e == null) ? 0 : e - 110;
  }

  onClickOnNext(e: number) {
    this.odTabs.scrollTo = (e == null) ? 10 : e + 110;
  }

  private addTabData(id: number, type: CustomerAppTypesVo, name: string, activated: boolean, position: number) {
    let tabData: TabData = new TabData();
    tabData.id = id;
    tabData.type = type;
    tabData.name = name;
    tabData.activated = activated;
    tabData.position = position;

    this._datas.set(new Pair<number, string>(id, type), tabData);
  }

  private getTabData(id: number, type: CustomerAppTypesVo): TabData {
    let keys = Array.from(this._datas.keys()).filter((key: Pair<number, string>) => (id == null || key.a == id) && (key.b == type || type == null));

    if (!CheckUtils.isEmpty(keys) && this._datas.has(keys[0])) {

      return this._datas.get(keys[0]);
    }
    return null;
  }

  private usersUpdated(updatesData: UpdatedAllModelVoData<number>) {
    if (CheckUtils.isEmpty(updatesData) || CheckUtils.areEmpty(updatesData.uids, this._decoratedAvatarData) ||
      updatesData.uids.indexOf(this._decoratedAvatarData.id) == -1) return;
    this.renderDecoratedAvatar(this._usersProvider.model.get(this._decoratedAvatarData.id));
  }

  private userUpdated(updatesData: UpdatedModelVoData<number>) {
    if (CheckUtils.isEmpty(updatesData) || CheckUtils.areEmpty(updatesData.uid, this._decoratedAvatarData) ||
      updatesData.uid != this._decoratedAvatarData.id) return;
    this.renderDecoratedAvatar(this._usersProvider.model.get(this._decoratedAvatarData.id));
  }

  private renderDecoratedAvatar(user: UserVoData) {
    this._decoratedAvatarData = UserConverter.convertToDecoratedAvatarData(user, ENV.fileUrl, this._decoratedAvatarData);
    this._decoratedAvatarData.hideStatus = true;
    this._decoratedAvatarData.hideType = true;
  }

  private applyColors() {
    if (this._mainColor != null && this._mainColor != undefined) {
      document.body.style.setProperty("--main-color", this._mainColor);
    }
    if (this._mainTextColor != null && this._mainTextColor != undefined) {
      document.body.style.setProperty("--main-text-color", this._mainTextColor);
    }
    if (this._secondaryButtonTextColor != null && this._secondaryButtonTextColor != undefined) {
      document.body.style.setProperty("--secondary-button-text-color", this._secondaryButtonTextColor);
    }
  }

}



// WEBPACK FOOTER //
// ./src/ui/components/header/header.ts